import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd'
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { errorTag, translateError } from '@/shared/api/errors'
import { useDeniedPlans, usePlanOptions } from '@/shared/api/reference'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, getCurrencyCode, todayInTenantZone } from '@/shared/lib/format'
import { SEARCH_DEBOUNCE_MS } from '@/shared/lib/localList'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { displayName } from '@/shared/lib/planName'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { useIdempotencyKey } from '@/shared/lib/useIdempotencyKey'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { TWO_COLUMNS } from '@/shared/ui/descriptionColumns'
import { MyCollectLimit } from '@/features/collectLimit/MyCollectLimit'
import { PageHeader } from '@/shared/ui/PageHeader'
import { issueGrant } from '@/features/subscriber/subscriberApi'
import {
  DIRECTORY_LIMIT,
  RECEIPT_PURPOSES,
  createReceipt,
  fetchCashBalance,
  fetchReceipts,
  searchDirectory,
  type DirectorySubscriber,
  type Receipt,
  type ReceiptPurpose,
  type ReceiptResult,
} from '@/features/cashdesk/cashdeskApi'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { CategorySplit, type SplitLine } from '@/features/cashdesk/CategorySplit'
import { fetchPaymentCategories } from '@/features/cashdesk/paymentCategoryApi'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import { useReceiptPrinter } from '@/features/cashdesk/useReceiptPrinter'
import { SaleSection } from '@/features/cashdesk/SaleSection'
import {
  fetchMyApprovedDiscounts,
  fetchQuote,
  requestDiscount,
  saleState,
  type Discount,
} from '@/features/cashdesk/salesApi'

interface PaymentForm {
  branchId?: string
  purpose: ReceiptPurpose
  amount: Money | null
  planId?: string
  serviceName?: string
  discount?: Money | null
  discountReason?: string
  discountId?: string | null
  allowCredit?: boolean
  lines?: SplitLine[]
}

const SERVICE_NAME_MAX = 120

export function CashdeskPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { can, canAll } = usePermissions()
  const queryClient = useQueryClient()
  const printer = useReceiptPrinter()
  const branches = useCashBranches()
  const [form] = Form.useForm<PaymentForm>()

  const [typed, setTyped] = useState('')
  const [term, setTerm] = useState('')
  const [subscriber, setSubscriber] = useState<DirectorySubscriber | null>(null)
  const [last, setLast] = useState<ReceiptResult | null>(null)
  const [attempt, setAttempt] = useState(0)

  const currency = user?.tenant.currency ?? getCurrencyCode()
  const canSellPackage = canAll('grant.issue', 'plan.read')
  const denied = useDeniedPlans(subscriber ? subscriber.branchNodeId : undefined, canSellPackage)
  const plans = usePlanOptions(canSellPackage).filter((plan) => plan.sellable)
    .filter((plan) => !denied.has(plan.id))

  useEffect(() => {
    if (typed === term) return
    const timer = setTimeout(() => setTerm(typed.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [typed, term])

  const directory = useQuery({
    queryKey: queryKeys.cashdesk.directory(term),
    queryFn: ({ signal }) => searchDirectory(term, signal),
    enabled: term.length >= 2,
    staleTime: 15_000,
  })

  const watchedBranch = Form.useWatch('branchId', form)
  const branchId = branches.sole?.id ?? watchedBranch ?? null
  const needsBranchChoice = !branches.unknown && branches.active.length > 1

  const balance = useQuery({
    queryKey: queryKeys.cashdesk.balance(branchId),
    queryFn: ({ signal }) => fetchCashBalance(branchId, signal),
    enabled: can('cash.balance.view') && branchId !== null,
    refetchInterval: 30_000,
  })

  const today = todayInTenantZone()
  const mine = useQuery({
    queryKey: queryKeys.cashdesk.myDay(today),
    queryFn: ({ signal }) => fetchReceipts({ mine: true, day: today }, 50, signal),
    enabled: can('cash.receipt.view'),
  })

  const purpose = Form.useWatch('purpose', form) ?? 'TOPUP'
  const amount = Form.useWatch('amount', form)
  const planId = Form.useWatch('planId', form)
  const serviceName = Form.useWatch('serviceName', form)
  const discountValue = Form.useWatch('discount', form)
  const discountId = Form.useWatch('discountId', form) ?? null
  const discountReason = Form.useWatch('discountReason', form)
  const allowCredit = Form.useWatch('allowCredit', form) ?? false
  const splitLines = Form.useWatch('lines', form) ?? []
  const [requestedFor, setRequestedFor] = useState<string | null>(null)
  const selling = purpose === 'TOPUP' && Boolean(planId) && subscriber !== null
  const discountMinor = selling ? (discountValue?.amount ?? 0) : 0
  const splittable = purpose === 'TOPUP' && !planId
  const lineSignature = splittable
    ? splitLines.map((line) => `${line?.categoryId ?? ''}=${line?.amount?.amount ?? ''}`).join(',')
    : ''
  const receiptKey = useIdempotencyKey(
    'cash-receipt',
    [
      attempt,
      subscriber?.id ?? '',
      branchId ?? '',
      purpose,
      amount?.amount ?? '',
      planId ?? '',
      serviceName ?? '',
      discountMinor,
      discountId ?? '',
      allowCredit ? 'credit' : '',
      lineSignature,
    ].join(':'),
  )

  const categories = useQuery({
    queryKey: queryKeys.paymentCategories,
    queryFn: ({ signal }) => fetchPaymentCategories(signal),
    staleTime: 5 * 60_000,
  })
  const activeCategories = (categories.data ?? []).filter((category) => category.active)

  const quote = useQuery({
    queryKey: queryKeys.cashdesk.quote(subscriber?.id ?? '', planId ?? '', branchId),
    queryFn: ({ signal }) => fetchQuote(subscriber?.id ?? '', planId ?? '', branchId, signal),
    enabled: selling,
    staleTime: 10_000,
  })

  const canApplyDiscount = can('cash.discount.apply')
  const approved = useQuery({
    queryKey: queryKeys.cashdesk.approvedDiscounts(subscriber?.id ?? ''),
    queryFn: ({ signal }) => fetchMyApprovedDiscounts(subscriber?.id ?? '', signal),
    enabled: selling && canApplyDiscount,
  })

  const sale = saleState(
    selling ? quote.data : undefined,
    amount?.amount ?? 0,
    discountMinor,
    discountId !== null,
    allowCredit,
  )
  const requestSignature = [subscriber?.id, planId, discountMinor].join(':')

  const askApproval = useMutation({
    mutationFn: () =>
      requestDiscount(
        {
          subscriberId: subscriber?.id ?? '',
          branchId,
          planId: planId ?? '',
          amount: discountMinor,
          reason: discountReason?.trim() ?? '',
        },
        newIdempotencyKey('cash-discount'),
      ),
    onSuccess: () => {
      setRequestedFor(requestSignature)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.discounts })
    },
  })

  const requestApproval = () => {
    submitOnValid(form, () => askApproval.mutate())
  }

  useEffect(() => {
    form.setFieldsValue({ discountId: null, allowCredit: false })
  }, [planId, subscriber?.id, form])

  const pickApproved = (picked: Discount | null) => {
    form.setFieldsValue({
      discount: picked ? picked.amount : null,
      discountReason: picked ? picked.reason : undefined,
    })
  }

  const pay = useMutation({
    mutationFn: (values: PaymentForm) =>
      createReceipt(
        {
          subscriberId: subscriber?.id ?? '',
          branchId,
          purpose: values.purpose,
          amount: values.amount?.amount ?? 0,
          planId: values.purpose === 'TOPUP' ? (values.planId ?? null) : null,
          serviceName: values.purpose === 'SERVICE' ? (values.serviceName?.trim() ?? null) : null,
          discount: discountMinor > 0 ? discountMinor : null,
          discountReason: discountMinor > 0 ? (values.discountReason?.trim() ?? null) : null,
          discountId: discountMinor > 0 ? (values.discountId ?? null) : null,
          allowCredit: selling && (values.allowCredit ?? false),
          lines: splittable
            ? (values.lines ?? []).map((line) => ({
                categoryId: line.categoryId ?? '',
                amount: line.amount?.amount ?? 0,
              }))
            : [],
        },
        receiptKey,
      ),
    onSuccess: (result) => {
      setLast(result)
      setAttempt((n) => n + 1)
      void queryClient.invalidateQueries({ queryKey: queryKeys.collectLimits.mine })
      form.setFieldsValue({
        amount: null,
        planId: undefined,
        serviceName: undefined,
        discount: null,
        discountReason: undefined,
        discountId: null,
        allowCredit: false,
        lines: [],
      })
      setRequestedFor(null)
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      void printer.print(result.receipt)
    },
  })

  const retryPackage = useMutation({
    mutationFn: (input: { subscriberId: string; planId: string; retryKey: string }) =>
      issueGrant(input.subscriberId, input.planId, true, input.retryKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
    },
  })

  const pickSubscriber = (id: string | undefined) => {
    setSubscriber((directory.data ?? []).find((row) => row.id === id) ?? null)
    setLast(null)
    pay.reset()
    retryPackage.reset()
    askApproval.reset()
    setRequestedFor(null)
  }

  const failedPurchase = last?.packagePurchase.status === 'FAILED' ? last.packagePurchase : null

  return (
    <>
      <PageHeader title={t('nav.cashdesk')} description={t('pageIntro.cashdesk')} />
      <MyCollectLimit />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card title={t('cashdesk.subscriber.title')}>
              <Select<string>
                showSearch
                allowClear
                filterOption={false}
                value={subscriber?.id}
                onSearch={setTyped}
                onChange={pickSubscriber}
                loading={directory.isFetching}
                placeholder={t('cashdesk.subscriber.searchPlaceholder')}
                notFoundContent={
                  term.length < 2 ? t('cashdesk.subscriber.typeMore') : t('cashdesk.subscriber.notFound')
                }
                style={{ width: '100%' }}
                options={(directory.data ?? []).map((row) => ({
                  value: row.id,
                  label: [row.login, row.displayName, row.customerId, row.phone].filter(Boolean).join(' · '),
                }))}
              />
              {directory.isError ? (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={translateError(directory.error, t)}
                />
              ) : null}
              {directory.isSuccess && directory.data.length === DIRECTORY_LIMIT ? (
                <Typography.Text type="secondary">
                  {t('cashdesk.subscriber.truncated', { count: DIRECTORY_LIMIT })}
                </Typography.Text>
              ) : null}

              {subscriber ? (
                <Descriptions size="small" column={TWO_COLUMNS} bordered style={{ marginTop: 12 }}>
                  <Descriptions.Item label={t('cashdesk.receipt.login')}>{subscriber.login}</Descriptions.Item>
                  <Descriptions.Item label={t('cashdesk.receipt.customerId')}>
                    {subscriber.customerId ?? t('common.dash')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('cashdesk.receipt.fullName')}>
                    {subscriber.displayName ?? t('common.dash')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('cashdesk.subscriber.plan')}>
                    {subscriber.planName ?? t('common.dash')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('cashdesk.subscriber.balance')}>
                    {subscriber.balance ? formatMoneyValue(subscriber.balance) : t('common.dash')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('cashdesk.subscriber.state')}>
                    <Tag>{t(`subscriberState.${subscriber.state}`, { defaultValue: subscriber.state })}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              ) : null}
            </Card>

            <Card title={t('cashdesk.payment.title')}>
              {branches.loaded && branches.active.length === 0 ? (
                <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('cashdesk.noBranch')} />
              ) : null}

              <Form<PaymentForm>
                form={form}
                layout="vertical"
                requiredMark
                initialValues={{ purpose: 'TOPUP', amount: null }}
                disabled={subscriber === null}
              >
                {needsBranchChoice ? (
                  <Form.Item
                    name="branchId"
                    label={t('cashdesk.branch')}
                    rules={[{ required: true, message: t('cashdesk.branchRequired') }]}
                  >
                    <CashBranchSelect branches={branches.active} loading={branches.isPending} disabled={false} />
                  </Form.Item>
                ) : null}

                <Form.Item name="purpose" label={t('cashdesk.payment.purpose')}>
                  <Radio.Group
                    optionType="button"
                    options={RECEIPT_PURPOSES.map((value) => ({
                      value,
                      label: t(`cashdesk.purpose.${value}`),
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  name="amount"
                  label={t('cashdesk.payment.amount')}
                  extra={t('cashdesk.payment.amountHint')}
                  rules={[
                    {
                      validator: (_, value: Money | null | undefined) =>
                        value && value.amount > 0
                          ? Promise.resolve()
                          : Promise.reject(new Error(t('cashdesk.payment.amountRequired'))),
                    },
                  ]}
                >
                  <MoneyInput currency={currency} minMinor={1} style={{ width: '100%' }} />
                </Form.Item>

                {purpose === 'TOPUP' && canSellPackage ? (
                  <Form.Item
                    name="planId"
                    label={t('cashdesk.payment.package')}
                    extra={t('cashdesk.payment.packageHint')}
                  >
                    <Select<string>
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t('cashdesk.payment.packagePlaceholder')}
                      options={plans.map((plan) => ({
                        value: plan.id,
                        label: `${displayName(plan, i18n.language)} — ${formatMoneyValue(plan.price)}`,
                      }))}
                    />
                  </Form.Item>
                ) : null}

                {splittable && activeCategories.length > 0 ? (
                  <CategorySplit
                    categories={activeCategories}
                    lines={splitLines}
                    total={amount?.amount ?? 0}
                    currency={currency}
                  />
                ) : null}

                {selling ? (
                  <SaleSection
                    quote={quote.data}
                    quoteLoading={quote.isPending}
                    quoteError={quote.isError ? quote.error : null}
                    state={sale}
                    discount={discountMinor}
                    discountId={discountId}
                    approved={approved.data ?? []}
                    canApplyDiscount={canApplyDiscount}
                    currency={currency}
                    onPickApproved={pickApproved}
                    onRequestApproval={requestApproval}
                    requesting={askApproval.isPending}
                    requestError={askApproval.isError ? askApproval.error : null}
                    requested={requestedFor === requestSignature}
                  />
                ) : null}

                {purpose === 'SERVICE' ? (
                  <Form.Item
                    name="serviceName"
                    label={t('cashdesk.payment.serviceName')}
                    rules={[
                      { required: true, whitespace: true, message: t('cashdesk.payment.serviceNameRequired') },
                      { max: SERVICE_NAME_MAX, message: t('cashdesk.payment.serviceNameTooLong') },
                    ]}
                  >
                    <Input maxLength={SERVICE_NAME_MAX} />
                  </Form.Item>
                ) : null}

                {pay.isError ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={translateError(pay.error, t)}
                    action={
                      errorTag(pay.error) === 'CASH_DISCOUNT_APPROVAL_REQUIRED' &&
                      requestedFor !== requestSignature ? (
                        <Button size="small" loading={askApproval.isPending} onClick={requestApproval}>
                          {t('sales.requestApproval')}
                        </Button>
                      ) : undefined
                    }
                  />
                ) : null}

                <Button
                  type="primary"
                  size="large"
                  loading={pay.isPending}
                  disabled={subscriber === null || (selling && (quote.isPending || sale.blocked))}
                  onClick={() => submitOnValid(form, (values) => pay.mutate(values))}
                >
                  {t('cashdesk.payment.submit')}
                </Button>
              </Form>
            </Card>

            {last ? (
              <Card title={t('cashdesk.result.title')}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    type={last.receipt.status === 'PARTIAL' ? 'warning' : 'success'}
                    showIcon
                    message={t('cashdesk.result.created', {
                      no: last.receipt.receiptNo,
                      amount: formatMoneyValue(last.receipt.amount),
                    })}
                    description={last.replay ? t('cashdesk.result.replay') : undefined}
                  />
                  {failedPurchase ? (
                    <Alert
                      type="warning"
                      showIcon
                      message={t('cashdesk.result.packageFailed')}
                      description={
                        <Space direction="vertical">
                          <span>
                            {failedPurchase.errorTag
                              ? t(`common.errorTag.${failedPurchase.errorTag}`, {
                                  defaultValue: failedPurchase.errorText ?? failedPurchase.errorTag,
                                })
                              : (failedPurchase.errorText ?? t('errors.generic'))}
                          </span>
                          {failedPurchase.planId && failedPurchase.retryKey && canSellPackage ? (
                            <Button
                              icon={<ReloadOutlined />}
                              loading={retryPackage.isPending}
                              disabled={retryPackage.isSuccess}
                              onClick={() =>
                                retryPackage.mutate({
                                  subscriberId: last.receipt.customer.subscriberId,
                                  planId: failedPurchase.planId ?? '',
                                  retryKey: failedPurchase.retryKey ?? '',
                                })
                              }
                            >
                              {t('cashdesk.result.retryPackage')}
                            </Button>
                          ) : null}
                        </Space>
                      }
                    />
                  ) : null}
                  {retryPackage.isError ? (
                    <Alert type="error" showIcon message={translateError(retryPackage.error, t)} />
                  ) : null}
                  {retryPackage.isSuccess ? (
                    <Alert type="success" showIcon message={t('cashdesk.result.packageIssued')} />
                  ) : null}
                  {printer.error ? (
                    <Alert type="error" showIcon message={translateError(printer.error, t)} />
                  ) : null}
                  <Button
                    icon={<PrinterOutlined />}
                    loading={printer.printingId === last.receipt.id}
                    onClick={() => void printer.print(last.receipt)}
                  >
                    {t('cashdesk.reprint')}
                  </Button>
                </Space>
              </Card>
            ) : null}
          </Space>
        </Col>

        <Col xs={24} lg={9}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {can('cash.balance.view') ? (
              <Card title={t('cashdesk.balance.title')} loading={balance.isLoading}>
                {balance.isError ? (
                  <Alert type="error" showIcon message={translateError(balance.error, t)} />
                ) : null}
                {balance.isSuccess ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Typography.Text type="secondary">
                      {balance.data.name} ({balance.data.code})
                    </Typography.Text>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic title={t('cashdesk.balance.cash')} value={formatMoneyValue(balance.data.cash)} />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('cashdesk.balance.inTransit')}
                          value={formatMoneyValue(balance.data.inTransit)}
                        />
                      </Col>
                    </Row>
                  </Space>
                ) : null}
                {branchId === null && branches.active.length > 0 ? (
                  <Typography.Text type="secondary">{t('cashdesk.balance.pickBranch')}</Typography.Text>
                ) : null}
                {branches.loaded && branches.active.length === 0 ? (
                  <Typography.Text type="secondary">{t('cashdesk.branches.empty')}</Typography.Text>
                ) : null}
              </Card>
            ) : null}

            {can('cash.receipt.view') ? (
              <Card title={t('cashdesk.today.title')}>
                {mine.isError ? <Alert type="error" showIcon message={translateError(mine.error, t)} /> : null}
                {mine.isSuccess && mine.data.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('cashdesk.today.empty')} />
                ) : null}
                {mine.isSuccess && mine.data.length > 0 ? (
                  <MyReceipts receipts={mine.data} onPrint={(r) => void printer.print(r)} printingId={printer.printingId} />
                ) : null}
              </Card>
            ) : null}
          </Space>
        </Col>
      </Row>

      {printer.portal}
    </>
  )
}

function MyReceipts({
  receipts,
  onPrint,
  printingId,
}: {
  receipts: readonly Receipt[]
  onPrint: (receipt: Receipt) => void
  printingId: string | null
}) {
  const { t } = useTranslation()
  const total = receipts.reduce((sum, r) => sum + r.amount.amount, 0)
  const currency = receipts[0]?.amount.currency ?? getCurrencyCode()
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Typography.Text strong>
        {t('cashdesk.today.total', { count: receipts.length, amount: formatMoneyValue({ amount: total, currency }) })}
      </Typography.Text>
      <Table<Receipt>
        size="small"
        pagination={false}
        rowKey={(row) => row.id}
        dataSource={[...receipts]}
        scroll={{ x: 360 }}
        columns={[
          {
            key: 'time',
            title: t('cashdesk.columns.date'),
            render: (_, row) => formatDateTime(row.createdAt),
          },
          { key: 'login', title: t('cashdesk.columns.customer'), render: (_, row) => row.customer.login },
          {
            key: 'amount',
            title: t('cashdesk.columns.amount'),
            align: 'right',
            render: (_, row) => formatMoneyValue(row.amount),
          },
          {
            key: 'print',
            title: '',
            width: 44,
            render: (_, row) => (
              <Button
                size="small"
                type="text"
                icon={<PrinterOutlined />}
                aria-label={t('cashdesk.reprint')}
                loading={printingId === row.id}
                onClick={() => onPrint(row)}
              />
            ),
          },
        ]}
      />
    </Space>
  )
}
